<script lang="ts">
import type { HTMLInputAttributes, HTMLTextareaAttributes } from 'svelte/elements';
import Textarea from '../../atoms/Textarea/Textarea.svelte';
import EmailInput from '../../atoms/TextInput/EmailInput.svelte';
import TelInput from '../../atoms/TextInput/TelInput.svelte';
import TextInput from '../../atoms/TextInput/TextInput.svelte';

interface Props extends Record<string, unknown> {
    className?: string;
    hidden?: boolean;
    type: string;
    placeholder?: string;
    autocompleteAttr?: HTMLInputAttributes['autocomplete'];
    name: string;
    id: string;
    role?: string;
    isInvalid?: boolean;
    customValidationMessage?: string;
    valueObject?: string;
    title?: string;
    required?: boolean;
    onfocus?: () => void;
}

let {
    className = '',
    hidden = false,
    type = 'text',
    placeholder,
    autocompleteAttr = '' as HTMLInputAttributes['autocomplete'],
    name,
    id,
    role,
    isInvalid = false,
    customValidationMessage,
    valueObject = $bindable(),
    ...rest
}: Props = $props();

// Separate element refs for each type to avoid type conflicts
let textInputElement: HTMLInputElement | undefined = $state();
let emailInputElement: HTMLInputElement | undefined = $state();
let telInputElement: HTMLInputElement | undefined = $state();
let textareaElement: HTMLTextAreaElement | undefined = $state();

// Unified getter for validation message
let validationMessage = $derived(() => {
    if (type === 'textarea') return textareaElement?.validationMessage ?? '';
    if (type === 'email') return emailInputElement?.validationMessage ?? '';
    if (type === 'tel') return telInputElement?.validationMessage ?? '';
    return textInputElement?.validationMessage ?? '';
});
</script>

<div class="input {className}" class:input--hidden={hidden}>
    {#if !hidden}
        <label for={id}>{placeholder}</label>
    {/if}
    {#if type === 'text'}
        <TextInput
            bind:inputElement={textInputElement}
            bind:valueObject
            {name}
            {autocompleteAttr}
            {type}
            {id}
            {role}
            {isInvalid}
            placeholder={placeholder && hidden ? placeholder : ''}
            {...rest}
        />
    {:else if type === 'email'}
        <EmailInput
            bind:inputElement={emailInputElement}
            bind:valueObject
            {name}
            {id}
            {type}
            {role}
            {isInvalid}
            placeholder={placeholder && hidden ? placeholder : ''}
            {...rest}
        />
    {:else if type === 'tel'}
        <TelInput
            bind:inputElement={telInputElement}
            bind:valueObject
            {name}
            {id}
            {type}
            {role}
            {isInvalid}
            placeholder={placeholder && hidden ? placeholder : ''}
            {...rest}
        />
    {:else if type === 'textarea'}
        <Textarea bind:inputElement={textareaElement} {autocompleteAttr} bind:valueObject {name} {role} {isInvalid} {id} {...rest} />
    {/if}
    {#if isInvalid}
        <div class="input__error">
            {#if customValidationMessage !== ''}
                {customValidationMessage}
            {:else}
                {validationMessage()}
            {/if}
        </div>
    {/if}
</div>

<style lang="scss">
    @use '/src/scss/global-constants/fonts';

    .input {
        position: relative;

        &--hidden {
            margin-bottom: 0;
        }

        &__error {
            color: var(--colour-sacrifice-altar);
            display: flex;
            flex-wrap: wrap;
            font-family: fonts.$fonts-secondary;
            font-size: 0.8rem;
            font-weight: fonts.$fonts-weight--normal;
            line-height: 1.4rem;
            text-align: center;
            top: 100%;
            width: 100%;
        }

        label {
            color: var(--colour-dreamless-sleep);
            font-family: fonts.$fonts-secondary;
            font-size: 1.6rem;
            font-weight: fonts.$fonts-weight--normal;
            line-height: 1.6;
            position: absolute;
            top: 0;
            transition:
                font-size var(--timing-duration-short),
                top var(--timing-duration-short);
        }

        :global(label:has(+ textarea:not(:placeholder-shown))),
        :global(label:has(+ input:not(:placeholder-shown))) {
            color: var(--colour-squant);
            font-size: 1.1rem;
            text-transform: uppercase;
            top: -1.2rem;
        }

        &:focus-within {
            label {
                color: var(--colour-squant);
                font-size: 1.1rem;
                top: -1.2rem;
                text-transform: uppercase;
            }
        }
    }
</style>
